import { useState, useEffect, useCallback } from 'react'
import { devicePrioritiesService } from '../services/devicePrioritiesService'
import { useAuthContext } from '../contexts/AuthContext'
import { DeviceInfo } from '../common/enums'

export interface DevicePriorityHookResult {
  priorityDeviceIds: string[]
  isPriorityDevice: (deviceId: string) => boolean
  getPriorityOrder: (deviceId: string) => number
  toggleDevicePriority: (deviceId: string, isPriority: boolean) => Promise<void>
  reorderPriorities: (deviceIds: string[]) => Promise<void>
  enhanceDevicesWithPriority: (devices: DeviceInfo[]) => DeviceInfo[]
  sortDevicesByPriority: (devices: DeviceInfo[]) => {
    priorityDevices: DeviceInfo[]
    regularDevices: DeviceInfo[]
  }
  isLoading: boolean
  error: any
}

/**
 * Hook for managing device priorities
 * Uses localStorage for browser-based persistence
 */
export function useDevicePriorities(): DevicePriorityHookResult {
  // Get current user from auth context
  const { email: userId } = useAuthContext()

  // State for localStorage-based priorities
  const [localPriorityIds, setLocalPriorityIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Load priorities from localStorage on mount and when user changes
  useEffect(() => {
    try {
      const storedIds = devicePrioritiesService.getPriorityDeviceIds(userId)
      setLocalPriorityIds(storedIds)
    } catch (error) {
      console.error('Failed to load device priorities:', error)
    } finally {
      setIsLoading(false)
    }
  }, [userId]) // Re-run when user changes

  // Use localStorage priorities
  const priorityDeviceIds = localPriorityIds

  // Helper functions
  const isPriorityDevice = useCallback(
    (deviceId: string): boolean => {
      return priorityDeviceIds.includes(deviceId)
    },
    [priorityDeviceIds]
  )

  const getPriorityOrder = useCallback(
    (deviceId: string): number => {
      const index = priorityDeviceIds.indexOf(deviceId)
      return index >= 0 ? index : -1
    },
    [priorityDeviceIds]
  )

  const toggleDevicePriority = useCallback(
    async (deviceId: string, isPriority: boolean): Promise<void> => {
      try {
        // Update localStorage immediately with user context
        const updatedIds = devicePrioritiesService.toggleDevicePriority(deviceId, isPriority, userId)
        setLocalPriorityIds(updatedIds)
      } catch (error) {
        console.error('Failed to toggle device priority:', error)
        // Revert localStorage on error
        setLocalPriorityIds(devicePrioritiesService.getPriorityDeviceIds(userId))
      }
    },
    [userId]
  )

  const reorderPriorities = useCallback(
    async (deviceIds: string[]): Promise<void> => {
      try {
        // Update localStorage immediately with user context
        const updatedIds = devicePrioritiesService.reorderPriorityDevices(deviceIds, userId)
        setLocalPriorityIds(updatedIds)
      } catch (error) {
        console.error('Failed to reorder device priorities:', error)
        // Revert localStorage on error
        setLocalPriorityIds(devicePrioritiesService.getPriorityDeviceIds(userId))
      }
    },
    [userId]
  )

  // Enhance devices with priority information
  const enhanceDevicesWithPriority = useCallback(
    (devices: DeviceInfo[]): DeviceInfo[] => {
      return devices.map((device) => ({
        ...device,
        isPriority: isPriorityDevice(device.sigfoxId),
        priorityOrder: getPriorityOrder(device.sigfoxId),
      }))
    },
    [isPriorityDevice, getPriorityOrder]
  )

  // Sort devices by priority (priority devices first, then regular devices)
  const sortDevicesByPriority = useCallback(
    (devices: DeviceInfo[]) => {
      const enhancedDevices = enhanceDevicesWithPriority(devices)

      const priorityDevices = enhancedDevices
        .filter((device) => device.isPriority)
        .sort((a, b) => (a.priorityOrder || 0) - (b.priorityOrder || 0))

      const regularDevices = enhancedDevices.filter((device) => !device.isPriority)

      return {
        priorityDevices,
        regularDevices,
      }
    },
    [enhanceDevicesWithPriority]
  )

  return {
    priorityDeviceIds,
    isPriorityDevice,
    getPriorityOrder,
    toggleDevicePriority,
    reorderPriorities,
    enhanceDevicesWithPriority,
    sortDevicesByPriority,
    isLoading,
    error: null, // No errors for localStorage implementation
  }
}

/**
 * Utility function to organize devices into priority and regular sections
 * with online/offline subsections
 */
export function organizeDevicesByPriorityAndStatus(
  devices: DeviceInfo[],
  deviceStatuses: Record<string, { status: string }>
): {
  priorityOnline: DeviceInfo[]
  priorityOffline: DeviceInfo[]
  regularOnline: DeviceInfo[]
  regularOffline: DeviceInfo[]
} {
  const priorityDevices = devices.filter((device) => device.isPriority)
  const regularDevices = devices.filter((device) => !device.isPriority)

  // Sort priority devices by their order
  const sortedPriorityDevices = priorityDevices.sort((a, b) => (a.priorityOrder || 0) - (b.priorityOrder || 0))

  return {
    priorityOnline: sortedPriorityDevices.filter((device) => deviceStatuses[device.sigfoxId]?.status === 'online'),
    priorityOffline: sortedPriorityDevices.filter((device) => deviceStatuses[device.sigfoxId]?.status !== 'online'),
    regularOnline: regularDevices.filter((device) => deviceStatuses[device.sigfoxId]?.status === 'online'),
    regularOffline: regularDevices.filter((device) => deviceStatuses[device.sigfoxId]?.status !== 'online'),
  }
}
